import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { FUENTE, useTema } from './tema';

/**
 * Confirmacion — preguntar antes de algo que no se deshace, dentro de la app.
 *
 * Sustituye a `window.confirm`. El cuadro del navegador funciona, pero no es de
 * esta aplicación: sale con los colores del sistema, encabezado por
 * «localhost:3000 dice», sin poder nombrar los botones —«Aceptar» y «Cancelar»
 * y nada más— y sin poder distinguir lo irreversible de lo rutinario. Quien lo
 * lee está a medio camino entre la app y el navegador, y en un aviso cuyo
 * propósito es que se lea antes de pulsar, eso importa.
 *
 * Además el del navegador **bloquea el hilo**: nada se repinta mientras está
 * abierto. Aquí no, y por eso el trato es una promesa.
 *
 * ── Cómo se usa ─────────────────────────────────────────────────────────
 *
 *   const [dialogo, confirmar] = useConfirmacion();
 *   …
 *   if (!await confirmar({ mensaje: '¿Seguro?' })) return;
 *   …
 *   return (<> {dialogo} … </>);
 *
 * La forma imita a `confirm` a propósito: las llamadas que ya existían solo
 * ganan un `await`, así que no hay que reordenar el código alrededor —que es
 * donde se cuelan los errores al cambiar algo que funcionaba—.
 */

const estilos = (C) => ({
  velo: {
    position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(2,6,23,.62)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18
  },
  caja: {
    width: 460, maxWidth: '100%',
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 12,
    boxShadow: '0 22px 60px rgba(0,0,0,.42)', padding: '20px 22px 17px'
  },
  titulo: { margin: '0 0 9px', fontSize: 16, fontWeight: 600, color: C.texto },
  mensaje: { margin: 0, fontSize: 13.5, lineHeight: 1.65, color: C.suave, whiteSpace: 'pre-line' },
  botones: { display: 'flex', justifyContent: 'flex-end', gap: 9, marginTop: 20 },
  boton: {
    padding: '8px 16px', borderRadius: 8, fontSize: 13.5, fontFamily: FUENTE,
    background: 'transparent', color: C.texto, border: `1px solid ${C.borde}`, cursor: 'pointer'
  },
  aceptar: (peligro) => ({
    background: peligro ? C.error : C.acento,
    borderColor: peligro ? C.error : C.acento,
    color: '#ffffff'
  })
});

const Confirmacion = ({
  titulo = null, mensaje, aceptar = 'Aceptar', cancelar = 'Cancelar',
  peligro = false, onResponder
}) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);
  const botonRef = useRef(null);

  useEffect(() => {
    // El foco entra en el diálogo para poder responder con el teclado sin
    // buscar el ratón, y porque dejarlo detrás permitiría seguir escribiendo en
    // la pantalla que el aviso está tapando.
    if (botonRef.current) botonRef.current.focus();

    const tecla = (e) => {
      if (e.key === 'Escape') onResponder(false);
      // Enter confirma solo porque el foco ya está en el botón de aceptar; sin
      // eso sería confirmar a ciegas lo que no se ha leído.
      if (e.key === 'Enter' && document.activeElement === botonRef.current) {
        e.preventDefault();
        onResponder(true);
      }
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [onResponder]);

  return (
    <div
      style={S.velo}
      // Pulsar fuera equivale a cancelar, nunca a aceptar: ante la duda, la
      // acción que no se deshace no ocurre.
      onClick={(e) => { if (e.target === e.currentTarget) onResponder(false); }}
    >
      <div style={S.caja} role="alertdialog" aria-modal="true" aria-label={titulo || 'Confirmar'}>
        {titulo && <h3 style={S.titulo}>{titulo}</h3>}
        <p style={S.mensaje}>{mensaje}</p>
        <div style={S.botones}>
          <button style={S.boton} onClick={() => onResponder(false)}>{cancelar}</button>
          <button
            ref={botonRef}
            style={{ ...S.boton, ...S.aceptar(peligro) }}
            onClick={() => onResponder(true)}
          >
            {aceptar}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Un `confirm` que devuelve una promesa y se pinta con el aspecto de la app.
 *
 * @returns {[React.ReactNode, Function]} lo que hay que pintar, y la función
 *   que pregunta. La función devuelve `true` si se acepta.
 */
export function useConfirmacion() {
  const [peticion, setPeticion] = useState(null);
  const responder = useRef(null);

  const confirmar = useCallback((opciones) => new Promise(resolve => {
    responder.current = resolve;
    setPeticion(typeof opciones === 'string' ? { mensaje: opciones } : opciones);
  }), []);

  const cerrar = useCallback((respuesta) => {
    setPeticion(null);
    // Se limpia antes de resolver: si quien espera vuelve a preguntar en el
    // mismo tick, esto ya no apunta a la respuesta anterior.
    const pendiente = responder.current;
    responder.current = null;
    if (pendiente) pendiente(respuesta);
  }, []);

  // Si el componente se desmonta con una pregunta abierta, quien esperaba se
  // quedaría colgado para siempre. Se responde que no: nadie dijo que sí.
  useEffect(() => () => {
    if (responder.current) { responder.current(false); responder.current = null; }
  }, []);

  const dialogo = peticion ? <Confirmacion {...peticion} onResponder={cerrar} /> : null;

  return [dialogo, confirmar];
}

export default Confirmacion;
